import { Injectable } from '@angular/core';
import { AuthorizationModel, MemberRolesAndPermissionsModel, UserRolesAndPermissionsModel } from '../../models/data/authorization.model';
import { ServiceResultModel } from '../../models/service/service-result.model';
import { ApiBaseUrls, ApiControllers } from '../../constants/constants';
import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Permission } from '../../enums/permissions.enums';
import { CommonChecks } from '../../utilities/common-checks.utilities';
import { ServiceUtilities } from '../../utilities/service.utilities';
import { RoleModel } from '../../models/data/role.model';
import { UserRoleModel } from '../../models/data/user-role.model';
import { APISanitizerUtilities } from '../../utilities/api-sanitizer.utilities';
import { StaffManagementMessagingConstants } from '../../constants/messaging.constants';
import { BYPASS_CACHE } from '../../interceptor/caching.interceptor';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.AUTHORIZATION()}`;

  constructor(private httpClient: HttpClient) { }

  public async getUserRolesAndPermissions(userId: number): Promise<ServiceResultModel<AuthorizationModel>> {
    try {
      const requestUrl = this.apiPath + `/${userId}`;
      const response = await firstValueFrom(this.httpClient.get<AuthorizationModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json' } }));

      const rolesAndPermissions = response.body ? <AuthorizationModel>response.body : undefined;
      return <ServiceResultModel>{data: rolesAndPermissions, success: true, message: "User roles and permissions retrieved successfully." };
    } catch (error: any) {
      // userLoadedGuard retries this call on a transient failure, which needs the transport status a
      // body-less 5xx or network error does not carry in its ProblemDetails.
      const result = ServiceUtilities.handleServiceException(error, "Failed to retrieve user roles and permissions.");
      result.statusCode ??= ServiceUtilities.resolveStatusCode(error);
      return result;
    }
  }

  // Role definitions are static reference data — nothing in this app creates or edits them — and
  // every caller only wants a name-to-roleId lookup, so the first successful fetch per
  // includeStaffRoles flag is cached for the session. Concurrent callers share the in-flight
  // request rather than each issuing their own, mirroring UserSessionService.getOrganizationSettings.
  private rolesCache = new Map<boolean, RoleModel[]>();
  private rolesRequests = new Map<boolean, Promise<ServiceResultModel<RoleModel[]>>>();

  public async getRoles(includeStaffRoles: boolean = false): Promise<ServiceResultModel<RoleModel[]>> {
    const cachedRoles = this.rolesCache.get(includeStaffRoles);
    if (cachedRoles) {
      return <ServiceResultModel>{ data: cachedRoles, success: true, message: "Roles retrieved successfully." };
    }

    const inFlightRequest = this.rolesRequests.get(includeStaffRoles);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const request = this.fetchRoles(includeStaffRoles);
    this.rolesRequests.set(includeStaffRoles, request);

    try {
      return await request;
    } finally {
      this.rolesRequests.delete(includeStaffRoles);
    }
  }

  private async fetchRoles(includeStaffRoles: boolean): Promise<ServiceResultModel<RoleModel[]>> {
    try {
      const requestUrl = this.apiPath + `/roles?includeStaffRoles=${includeStaffRoles}`;
      const response = await firstValueFrom(
        this.httpClient.get<RoleModel[]>(requestUrl, {
          observe: "response",
          headers: { 'Accept': 'application/json' }
        })
      );

      const roles = <RoleModel[]>response.body?.valueOf();
      if (roles) {
        this.rolesCache.set(includeStaffRoles, roles);
      }
      return <ServiceResultModel>{ data: roles, success: true, message: "Roles retrieved successfully." };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error, "Failed to retrieve roles.");
    }
  }

  /**
   * Every user-level role assignment in the system, one entry per user and role.
   *
   * BYPASS_CACHE because this answer decides which actions the page offers, and CacheInterceptor
   * would otherwise serve a copy for up to its TTL — long enough for another administrator's grant
   * to leave this page authorizing against assignments that no longer exist. Our own mutations
   * already evict it, but someone else's cannot.
   */
  public async getUserRoles(): Promise<ServiceResultModel<UserRoleModel[]>> {
    try {
      const requestUrl = this.apiPath + `/user-roles`;
      const response = await firstValueFrom(
        this.httpClient.get<UserRoleModel[]>(requestUrl, {
          observe: "response",
          headers: { 'Accept': 'application/json' },
          context: new HttpContext().set(BYPASS_CACHE, true)
        })
      );

      return <ServiceResultModel>{ data: <UserRoleModel[]>response.body?.valueOf(), success: true };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error, "Failed to retrieve staff role assignments.");
    }
  }

  // User-level (staff) role grants. The API authorizes each role separately — roles.sys_admin,
  // roles.staff_manager or roles.staff for the role being granted — and writes the target user's
  // Activity Log entry itself.
  public async addRolesToUser(userId: number, roleIds: number[]): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/roles`;
      const response = await firstValueFrom(
        this.httpClient.post<void>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<{ roleIds: number[] }>({ roleIds: roleIds })),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel>{
        success: true,
        message: StaffManagementMessagingConstants.STAFF_ADD_ROLE_SUCCESS(),
        statusCode: response.status
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async removeRolesFromUser(userId: number, roleIds: number[]): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/roles`;
      const response = await firstValueFrom(
        this.httpClient.delete<void>(
          requestUrl,
          {
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<{ roleIds: number[] }>({ roleIds: roleIds })),
            observe: "response",
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          }
        ));

      return <ServiceResultModel>{
        success: true,
        message: StaffManagementMessagingConstants.STAFF_ROLE_REMOVAL_SUCCESS(),
        statusCode: response.status
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public hasPermission(
    userContext: AuthorizationModel, 
    requiredPermission: Permission, 
    organizationId?: number,
    userPermissionOnly: boolean = false
  ): boolean {
    if (userContext != null && !CommonChecks.isNullOrUndefinedOrEmptyOrWhitespace(requiredPermission)) {
      if (this.hasPermissionInList(userContext.userPermissions?.permissions, requiredPermission)) {
        return true;
      }

      if (userPermissionOnly) {
        return false;
      }

      if (organizationId) {
        return this.hasMemberPermission(userContext.memberPermissions, requiredPermission, organizationId);
      }

      if (userContext.memberPermissions) {
        for (const memberPermission of userContext.memberPermissions) {
          if (this.hasPermissionInList(memberPermission.permissions, requiredPermission)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  public hasUserPermission(userPermissions: UserRolesAndPermissionsModel | undefined, permission: Permission): boolean {
    return this.hasPermissionInList(userPermissions?.permissions, permission);
  }

  public hasMemberPermission(
    memberPermissions: MemberRolesAndPermissionsModel[] | undefined,
    permission: Permission,
    organizationId: number | undefined
  ): boolean {
    const memberPermission = memberPermissions?.find(mp => mp.organizationId === organizationId);
    return this.hasPermissionInList(memberPermission?.permissions, permission);
  }

  public hasPermissionInList(
    permissions: string[] | undefined,
    requiredPermission: Permission
  ): boolean {
    if (!permissions || permissions.length === 0) {
      return false;
    }

    if (permissions.includes(requiredPermission)) {
      return true;
    }

    const resourcePart = this.getResourceFromPermission(requiredPermission);
    if (resourcePart) {
      const wildcardPermission = `${resourcePart}.*`;
      return permissions.includes(wildcardPermission);
    }

    return false;
  }

  private getResourceFromPermission(permission: string): string | undefined {
    const lastDotIndex = permission.lastIndexOf('.');
    return lastDotIndex > 0 ? permission.substring(0, lastDotIndex) : undefined;
  }

  public isStaff(userContext: AuthorizationModel | null): boolean {
    if (!userContext?.userPermissions) {
      return false;
    }

    const hasUserPermissions = userContext.userPermissions.permissions?.length > 0;
    const hasUserRoles = userContext.userPermissions.roles?.length > 0;

    return hasUserPermissions || hasUserRoles;
  }
}
